import { computed, type ComputedRef } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import type { MenuItem, MenuItemWithRelatedPaths } from '@cplug/frontend-core/composables'
import { isNewUser } from '@cplug/frontend-subscription-hub/domain/plans/my-plan'
import { storeToRefs } from 'pinia'
import { useWhitelabelStore } from '@cplug/frontend-shared-domains/domain/whitelabel'
import { BaseIconName } from '@cplug/design-system/components/props'
import { useRedirectToSubscriptionHub } from './useRedirectToSubscriptionHub'
import useOpenPreviousVersionModal from './useOpenPreviousVersionModal'
import { isOnlineStoreMenuEnabled, isOnlineStoreRoute } from './isOnlineStoreMenuEnabled'
import { openLegacySSO } from '~/shared/composables/useLegacySSORedirect'
import { useTranslation } from '~/domain/shared/navigation/composables/useMenuOptions.i18n'
import { useBrandStore } from '~/domain/shared/branding/stores/useBrandStore'

export type MenuOptions = {
  mainMenu: ComputedRef<MenuItemWithRelatedPaths[]>
  salesChannelMenu: ComputedRef<MenuItemWithRelatedPaths[]>
  bottomMenu: ComputedRef<MenuItemWithRelatedPaths[]>
}

export function useMenuOptions (): MenuOptions {
  const { t } = useTranslation()
  const router = useRouter()
  const route = useRoute()
  const whitelabelStore = useWhitelabelStore()
  const brandStore = useBrandStore()
  const { isPartnerBrand } = storeToRefs(brandStore)
  const { isPartner, isWhitelabel } = storeToRefs(whitelabelStore)
  const isStoreApp = computed(() =>
    isOnlineStoreMenuEnabled() && isOnlineStoreRoute(route.path)
  )
  const navigateTo = (path: string) => (): void => {
    router.push(path)
  }

  const redirectToLabelModels = (): void => {
    openLegacySSO('etiquetas').catch(() => {})
  }

  const erpMainMenu = computed<MenuItemWithRelatedPaths[]>(() => [
    {
      label: t('Overview'),
      icon: BaseIconName.Home,
      name: 'overview',
      path: '/dashboard/',
      exactMatch: true,
      command: navigateTo('/dashboard/')
    },
    {
      label: t('Sales'),
      icon: BaseIconName.CartArrowDown,
      name: 'sales',
      path: '/dashboard/sale',
      items: getSalesMenu(navigateTo, t)
    },
    {
      label: t('Products'),
      icon: BaseIconName.Tag,
      name: 'products',
      path: '/dashboard/catalog',
      items: getProductsMenu(navigateTo, t)
    },
    {
      label: t('Stock'),
      icon: BaseIconName.Box,
      name: 'stock',
      path: '/dashboard/stock',
      items: getStockMenu(navigateTo, t)
    },
    {
      label: t('Customers'),
      icon: BaseIconName.Users,
      name: 'customers',
      path: '/dashboard/customers',
      items: getCustomersMenu(navigateTo, t)
    },
    {
      label: t('Finance'),
      icon: BaseIconName.Dollar,
      name: 'finance',
      path: '/dashboard/finance',
      items: getFinanceMenu(navigateTo, t)
    },
    {
      label: t('Reports'),
      icon: BaseIconName.ChartBar,
      name: 'reports',
      path: '/dashboard/report',
      items: getReportsMenu(navigateTo, t)
    }
  ])

  const salesChannelMenu = computed<MenuItemWithRelatedPaths[]>(() => [
    {
      title: t('Apps'),
      label: t('PointOfSale'),
      icon: BaseIconName.Desktop,
      name: 'point-of-sale',
      path: '/dashboard/fisical-operations',
      items: getPointOfSaleMenu(navigateTo, t)
    },
    {
      label: 'Dispositivos',
      icon: BaseIconName.ThLarge,
      name: 'apps',
      items: [
        {
          items: [
            {
              label: 'Terminais & Dispositivos',
              icon: BaseIconName.Desktop,
              path: '/dashboard/apps',
              name: 'apps-list',
              command: navigateTo('/dashboard/apps')
            }
          ]
        },
        {
          label: 'INSTALAÇÃO',
          items: [
            {
              label: 'Baixar aplicativos',
              icon: BaseIconName.Download,
              path: '/dashboard/download-apps',
              name: 'download-apps',
              command: navigateTo('/dashboard/download-apps')
            }
          ]
        }
      ]
    }
  ])

  const bottomMenu = computed<MenuItemWithRelatedPaths[]>(() => {
    if (isStoreApp.value) {
      return []
    }

    return [
      ...(!isNewUser()
        ? [{
            label: t('PreviousVersion'),
            icon: BaseIconName.ArrowCircleLeft,
            command: (): void => useOpenPreviousVersionModal(t('PreviousVersion'), t('Confirm'))
          }]
        : []),
      ...(!isPartner.value && !isWhitelabel.value && !isPartnerBrand.value
        ? [{
            label: t('MyPlan'),
            icon: BaseIconName.CreditCard,
            name: 'plans',
            path: '/dashboard/plans',
            command: (): void => useRedirectToSubscriptionHub()
          }]
        : []),
      {
        label: t('Settings'),
        icon: BaseIconName.Cog,
        name: 'settings',
        path: '/dashboard/settings',
        items: getSettingsMenu(navigateTo, t, redirectToLabelModels)
      }
    ]
  })

  const mainMenu = computed<MenuItemWithRelatedPaths[]>(() =>
    isStoreApp.value ? getStoreMenu(navigateTo, t) : erpMainMenu.value
  )

  return {
    mainMenu,
    salesChannelMenu: computed(() => isStoreApp.value ? [] : salesChannelMenu.value),
    bottomMenu
  }
}

function getStoreMenu (navigateTo: (path: string) => () => void, t: (key: string) => string): MenuItemWithRelatedPaths[] {
  return [
    {
      label: t('Overview'),
      icon: BaseIconName.Shop,
      path: '/dashboard/store/general',
      name: 'store-general',
      command: navigateTo('/dashboard/store/general')
    },
    {
      label: t('StoreConfiguration'),
      icon: BaseIconName.Cog,
      path: '/dashboard/store/settings',
      name: 'store-settings',
      command: navigateTo('/dashboard/store/settings')
    },
    {
      label: t('StoreCustomization'),
      icon: BaseIconName.Palette,
      path: '/dashboard/store/customize',
      name: 'store-customize',
      command: navigateTo('/dashboard/store/customize')
    },
    {
      label: t('OrderingModes'),
      icon: BaseIconName.Truck,
      path: '/dashboard/store/fulfillment',
      name: 'store-fulfillment',
      command: navigateTo('/dashboard/store/fulfillment')
    },
    // {
    //   label: t('DeliveryAreas'),
    //   icon: BaseIconName.MapMarker,
    //   path: '/dashboard/store/delivery-areas',
    //   name: 'store-delivery-areas',
    //   command: navigateTo('/dashboard/store/delivery-areas')
    // },
    {
      label: t('StorePayments'),
      icon: BaseIconName.Wallet,
      path: '/dashboard/store/payments',
      name: 'store-payments',
      command: navigateTo('/dashboard/store/payments')
    },
    {
      label: t('Products'),
      icon: BaseIconName.Tag,
      path: '/dashboard/store/products',
      name: 'store-products',
      command: navigateTo('/dashboard/store/products')
    }
  ]
}

function getProductsMenu (navigateTo: (path: string) => () => void, _t: any): MenuItem[] {
  return [
    {
      items: [
        {
          label: 'Produtos',
          icon: BaseIconName.Tag, // ícone de etiqueta
          path: '/dashboard/catalog/products',
          name: 'products',
          command: navigateTo('/dashboard/catalog/products')
        },
        {
          label: 'Fichas técnicas',
          icon: BaseIconName.Clipboard, // ícone de documento/lista
          path: '/dashboard/catalog/technical-sheets',
          name: 'technical-sheets',
          command: navigateTo('/dashboard/catalog/technical-sheets')
        },
        {
          label: 'Variações e opções',
          icon: BaseIconName.ThLarge, // ícone de variações (ajustar para o mais próximo disponível)
          path: '/dashboard/catalog/variations-and-options',
          name: 'variations-and-options',
          command: navigateTo('/dashboard/catalog/variations-and-options')
        }
      ]
    },
    {
      label: 'GERAL',
      items: [
        {
          label: 'Categorias',
          icon: BaseIconName.ObjectsColumn, // ícone de quadrados
          path: '/dashboard/catalog/categories',
          name: 'categories',
          command: navigateTo('/dashboard/catalog/categories')
        },
        {
          label: 'Unidades de medida',
          icon: BaseIconName.Database, // ícone de medidor/disco (ajustar para o mais próximo disponível)
          path: '/dashboard/catalog/unit-of-measurement',
          name: 'unit-of-measurement',
          command: navigateTo('/dashboard/catalog/unit-of-measurement')
        },
        {
          label: 'Lista de preços',
          icon: BaseIconName.List, // ícone de lista
          path: '/dashboard/catalog/price-lists', // ajustar path conforme necessário
          name: 'price-lists',
          command: navigateTo('/dashboard/catalog/price-lists')
        },
        {
          label: 'Parâmetros fiscais',
          icon: BaseIconName.Percentage, // ícone de porcentagem/imposto
          path: '/dashboard/tax-settings',
          name: 'tax-settings',
          command: navigateTo('/dashboard/tax-settings')
        }
      ]
    }
  ]
}

function getSalesMenu (navigateTo: (path: string) => () => void, _t: any): MenuItemWithRelatedPaths[] {
  return [
    {
      items: [
        {
          label: 'Pedidos de venda',
          icon: BaseIconName.Inbox, // ícone de caixa
          path: '/dashboard/sale/sale-orders',
          name: 'sale-orders',
          command: navigateTo('/dashboard/sale/sale-orders')
        },
        {
          label: 'Vendas',
          icon: BaseIconName.CartArrowDown, // cifrão com seta para baixo (ou o mais próximo)
          path: '/dashboard/sale/sales',
          name: 'sales',
          command: navigateTo('/dashboard/sale/sales')
        }
      ]
    },
    {
      label: 'FISCAL',
      items: [
        {
          label: 'NF-e',
          icon: BaseIconName.File, // ícone de documento fiscal
          path: '/dashboard/sale/outbound-invoices',
          name: 'outbound-invoices',
          exactMatch: true,
          relatedPaths: [
            '/dashboard/sale/outbound-invoices/edit',
            '/dashboard/sale/outbound-invoices/new-nfe',
            '/dashboard/sale/outbound-invoices/new-nfce',
            '/dashboard/sale/outbound-invoices/legacy'
          ],
          command: navigateTo('/dashboard/sale/outbound-invoices')
        },
        {
          label: 'SAT CF-e',
          icon: BaseIconName.File, // ícone de documento fiscal (usar o mesmo para ambos)
          path: '/dashboard/sale/sat-cfe', // ajustar path conforme necessário
          name: 'sat-cfe',
          command: navigateTo('/dashboard/sale/sat-cfe')
        }
      ]
    },
    {
      label: 'BENEFÍCIOS',
      items: [
        {
          label: 'Promoções',
          icon: BaseIconName.Megaphone, // ícone de medalha/estrela
          path: '/dashboard/sale/promotions',
          name: 'promotions',
          command: navigateTo('/dashboard/sale/promotions')
        }
      ]
    },
    {
      label: 'ORDEMS',
      items: [
        {
          label: 'Ordem de serviços',
          icon: BaseIconName.File, // ícone de documento assinado/contrato
          path: '/dashboard/sale/service/orders',
          name: 'orders',
          command: navigateTo('/dashboard/sale/service/orders')
        },
        {
          label: 'Contrato de vendas',
          icon: BaseIconName.FileEdit, // ícone de documento
          path: '/dashboard/sale/service/contracts',
          name: 'contracts',
          command: navigateTo('/dashboard/sale/service/contracts')
        }
      ]
    }
  ]
}

function getFinanceMenu (navigateTo: (path: string) => () => void, _t: any): MenuItem[] {
  return [
    {
      items: [
        {
          label: 'Extratos',
          icon: BaseIconName.Receipt, // ícone de recibo/documento
          path: '/dashboard/finance/statements',
          name: 'statements',
          command: navigateTo('/dashboard/finance/statements')
        },
        {
          label: 'Lançamentos',
          icon: BaseIconName.FileImport, // ícone de setas circulares
          path: '/dashboard/finance/financial-transaction',
          name: 'financial-transaction',
          command: navigateTo('/dashboard/finance/financial-transaction')
        },
        {
          label: 'Conciliação bancária',
          icon: BaseIconName.Bank, // ícone de banco (ajustar para o mais próximo disponível)
          path: '/dashboard/finance/bank-reconciliations',
          name: 'bank-reconciliations',
          command: navigateTo('/dashboard/finance/bank-reconciliations')
        },
        {
          label: 'Relatório de resultados',
          icon: BaseIconName.ChartLine, // ícone de gráfico
          path: '/dashboard/finance/results',
          name: 'results',
          command: navigateTo('/dashboard/finance/results')
        }
        // Hidden: boletos Cora — não exibir no menu
        // {
        //   label: 'Boletos',
        //   icon: BaseIconName.Barcode,
        //   path: '/dashboard/finance/bank-slips',
        //   name: 'bank-slips',
        //   command: navigateTo('/dashboard/finance/bank-slips')
        // }
      ]
    },
    {
      label: 'ORGANIZAÇÃO FINANCEIRA',
      items: [
        {
          label: 'Contrato de cartões e outros',
          icon: BaseIconName.CreditCard, // ícone de cartão
          path: '/dashboard/finance/card-contracts',
          name: 'card-contracts',
          command: navigateTo('/dashboard/finance/card-contracts')
        },
        {
          label: 'Contas bancárias',
          icon: BaseIconName.Bank, // ícone de banco
          path: '/dashboard/finance/bank-accounts',
          name: 'bank-accounts',
          command: navigateTo('/dashboard/finance/bank-accounts')
        },
        {
          label: 'Grupo Financeiro',
          icon: BaseIconName.Dollar, // ícone de cifrão em círculo
          path: '/dashboard/finance/financial-groups',
          name: 'financial-groups',
          command: navigateTo('/dashboard/finance/financial-groups')
        },
        {
          label: 'Plano de contas',
          icon: BaseIconName.Dollar, // ícone de cifrão em círculo
          path: '/dashboard/finance/account-plans',
          name: 'account-plans',
          command: navigateTo('/dashboard/finance/account-plans')
        },
        {
          label: 'Centro de custo',
          icon: BaseIconName.Dollar, // ícone de cifrão em círculo (usar o mesmo)
          path: '/dashboard/finance/cost-centers', // ajustar path conforme necessário
          name: 'cost-centers',
          command: navigateTo('/dashboard/finance/cost-centers')
        }
      ]
    },
    {
      label: 'NOTAS FISCAIS',
      items: [
        {
          label: 'Facilita NF-e',
          icon: BaseIconName.FileCheck, // ícone de documento com check
          path: '/dashboard/finance/nfe',
          name: 'finance-nfe',
          command: navigateTo('/dashboard/finance/nfe')
        },
        {
          label: 'Contabilidade',
          icon: BaseIconName.Edit, // ícone de lápis
          path: '/dashboard/finance/accountings',
          name: 'accountings',
          command: navigateTo('/dashboard/finance/accountings')
        }
      ]
    }
  ]
}

function getStockMenu (navigateTo: (path: string) => () => void, _t: any): MenuItem[] {
  return [
    {
      items: [
        {
          label: 'Estoque',
          icon: BaseIconName.Box, // ícone de caixas/quadrados
          path: '/dashboard/stock/stocks',
          name: 'stocks',
          command: navigateTo('/dashboard/stock/stocks')
        },
        {
          label: 'Movimentações',
          icon: BaseIconName.ArrowRightArrowLeft, // ícone de caixa com seta (ajustar para o mais próximo disponível)
          path: '/dashboard/stock/movements',
          name: 'movements',
          command: navigateTo('/dashboard/stock/movements')
        },
        {
          label: 'Transferências',
          icon: BaseIconName.ArrowRight, // ícone de seta circular/diagonal (ajustar para o mais próximo disponível)
          path: '/dashboard/stock/storage-locations/transfers',
          name: 'transfers',
          command: navigateTo('/dashboard/stock/storage-locations/transfers')
        },
        {
          label: 'Compras',
          icon: BaseIconName.ArrowLeft, // ícone de seta circular/diagonal (ajustar para o mais próximo disponível)
          path: '/dashboard/stock/purchases',
          name: 'purchases',
          command: navigateTo('/dashboard/stock/purchases')
        }
      ]
    },
    {
      label: 'LOGÍSTICA',
      items: [
        {
          label: 'Transportadoras',
          icon: BaseIconName.Truck, // ícone de caminhão
          path: '/dashboard/stock/carriers',
          name: 'carriers',
          command: navigateTo('/dashboard/stock/carriers')
        },
        {
          label: 'Fornecedores',
          icon: BaseIconName.Users, // ícone de pessoa (ajustar para o mais próximo disponível)
          path: '/dashboard/stock/suppliers',
          name: 'suppliers',
          command: navigateTo('/dashboard/stock/suppliers')
        }
      ]
    },
    {
      label: 'COMPRAS',
      items: [
        {
          label: 'NF-e de entrada',
          icon: BaseIconName.FileImport, // ícone de documento fiscal com seta para baixo (ajustar para o mais próximo disponível)
          path: '/dashboard/stock/invoices',
          name: 'stock-invoices',
          command: navigateTo('/dashboard/stock/invoices')
        },
        {
          label: 'Facilita NF-e',
          icon: BaseIconName.FileCheck, // ícone de documento com check
          path: '/dashboard/finance/nfe',
          name: 'finance-nfe',
          command: navigateTo('/dashboard/finance/nfe')
        }
      ]
    },
    {
      label: 'PRODUÇÃO',
      items: [
        {
          label: 'Pedido produção',
          icon: BaseIconName.ListCheck, // ícone de calendário/lista
          path: '/dashboard/stock/production-orders',
          name: 'production-orders',
          command: navigateTo('/dashboard/stock/production-orders')
        },
        {
          label: 'Produção',
          icon: BaseIconName.Clipboard, // ícone de relógio/processo
          path: '/dashboard/stock/productions',
          name: 'productions',
          command: navigateTo('/dashboard/stock/productions')
        },
        {
          label: 'Finalização',
          icon: BaseIconName.CalendarClock, // ícone de carrinho
          path: '/dashboard/stock/completions',
          name: 'completions',
          command: navigateTo('/dashboard/stock/completions')
        }
      ]
    },
    {
      label: 'CONFIGURAÇÕES',
      items: [
        {
          label: 'Categorias de Movimentação',
          icon: BaseIconName.ThLarge, // ícone de lista/carrinho
          path: '/dashboard/stock/movements-category',
          name: 'movements-category',
          command: navigateTo('/dashboard/stock/movements-category')
        }
      ]
    }
  ]
}

function getReportsMenu (navigateTo: (path: string) => () => void, _t: any): MenuItem[] {
  return [
    {
      label: 'Financeiro',
      icon: BaseIconName.Dollar,
      agroup: true,
      items: [
        {
          label: 'Análise de centro de custo',
          path: '/dashboard/report/cost-center-analysis',
          name: 'cost-center-analysis',
          command: navigateTo('/dashboard/report/cost-center-analysis')
        },
        {
          label: 'Fechamento de caixa',
          path: '/dashboard/report/cash-movements',
          name: 'cash-movements',
          command: navigateTo('/dashboard/report/cash-movements')
        },
        {
          label: 'Vendas',
          path: '/dashboard/report/sales',
          name: 'report-sales',
          command: navigateTo('/dashboard/report/sales')
        },
        {
          label: 'Despesas',
          path: '/dashboard/report/expenses',
          name: 'report-expenses',
          command: navigateTo('/dashboard/report/expenses')
        },
        {
          label: 'Lucro',
          path: '/dashboard/report/profits',
          name: 'profits',
          command: navigateTo('/dashboard/report/profits')
        },
        {
          label: 'Compras',
          path: '/dashboard/report/purchases',
          name: 'report-purchases',
          command: navigateTo('/dashboard/report/purchases')
        },
        {
          label: 'Fluxo de caixa',
          path: '/dashboard/report/cash-flow',
          name: 'cash-flow',
          command: navigateTo('/dashboard/report/cash-flow')
        },
        {
          label: 'Relatório básico',
          path: '/dashboard/report/basic-sales',
          name: 'basic-sales',
          command: navigateTo('/dashboard/report/basic-sales')
        },
        {
          label: 'Análise de Portfólio',
          path: '/dashboard/report/portfolio-analysis',
          name: 'portfolio-analysis',
          command: navigateTo('/dashboard/report/portfolio-analysis')
        },
        {
          label: 'Promoção',
          path: '/dashboard/report/promotions',
          name: 'report-promotions',
          command: navigateTo('/dashboard/report/promotions')
        },
        {
          label: 'Troca',
          path: '/dashboard/report/exchange',
          name: 'exchange-report',
          command: navigateTo('/dashboard/report/exchange')
        }
      ]
    },
    {
      label: 'Logística',
      icon: BaseIconName.Warehouse,
      agroup: true,
      items: [
        {
          label: 'Estoque',
          path: '/dashboard/report/stock',
          name: 'report-stock',
          command: navigateTo('/dashboard/report/stock')
        }
      ]
    },
    {
      label: 'Clientes',
      icon: BaseIconName.Users,
      agroup: true,
      items: [
        {
          label: 'Crédito de clientes',
          path: '/dashboard/report/client-credit',
          name: 'client-credit',
          command: navigateTo('/dashboard/report/client-credit')
        },
        {
          label: 'Atendimento de mesas',
          path: '/dashboard/report/table-services',
          name: 'table-services',
          command: navigateTo('/dashboard/report/table-services')
        }
      ]
    },
    {
      label: 'Segurança',
      icon: BaseIconName.Lock,
      agroup: true,
      items: [
        {
          label: 'Logs de segurança',
          path: '/dashboard/report/user-logs',
          name: 'user-logs',
          command: navigateTo('/dashboard/report/user-logs')
        },
        {
          label: 'Abandono de vendas',
          path: '/dashboard/report/sales-abandonments',
          name: 'sales-abandonments',
          command: navigateTo('/dashboard/report/sales-abandonments')
        }
      ]
    },
    {
      label: 'Produção',
      icon: BaseIconName.Clipboard,
      agroup: true,
      items: [
        {
          label: 'KDS',
          path: '/dashboard/report/kds',
          name: 'report-kds',
          command: navigateTo('/dashboard/report/kds')
        }
      ]
    },
    {
      label: 'SAT',
      icon: BaseIconName.Desktop,
      agroup: true,
      items: [
        {
          label: 'Status',
          path: '/dashboard/report/sat-status',
          name: 'sat-status',
          command: navigateTo('/dashboard/report/sat-status')
        }
      ]
    },
    {
      items: [
        {
          label: 'Relatório dinâmico',
          icon: BaseIconName.ChartLine,
          path: '/dashboard/report/self-service',
          name: 'report-self-service',
          command: navigateTo('/dashboard/report/self-service')
        },
        {
          label: 'Acessar Business Intelligence',
          icon: BaseIconName.ChartLine,
          path: '/dashboard/report/bi',
          name: 'report-bi',
          command: navigateTo('/dashboard/report/bi')
        }
      ]
    }
  ]
}

function getSettingsMenu (navigateTo: (path: string) => () => void, _t: any, redirectToLabelModels: () => void): MenuItemWithRelatedPaths[] {
  return [
    {
      items: [
        {
          label: 'Dados do negócio',
          icon: BaseIconName.Bank, // ícone de prédio/empresa
          path: '/dashboard/settings/business',
          name: 'business',
          command: navigateTo('/dashboard/settings/business')
        },
        {
          label: 'Unidades & Filiais',
          icon: BaseIconName.Briefcase, // ícone de caixa/loja
          path: '/dashboard/settings/companies',
          name: 'companies',
          command: navigateTo('/dashboard/settings/companies')
        },
        {
          label: 'Formas de pagamento',
          icon: BaseIconName.Wallet, // ícone de cartão
          path: '/dashboard/payments',
          name: 'payments',
          command: navigateTo('/dashboard/payments')
        },
        {
          label: 'Usuários & Permissões',
          icon: BaseIconName.UserEdit, // ícone de usuário com crachá
          path: '/dashboard/settings/users', // ajustar path conforme necessário
          name: 'users',
          command: navigateTo('/dashboard/settings/users'),
          relatedPaths: ['/dashboard/settings/permission-groups']
        },
        {
          label: 'Periféricos',
          icon: BaseIconName.Print, // ícone de quadrados com +
          path: '/dashboard/app/peripherals', // ajustar path conforme necessário
          name: 'peripherals',
          command: navigateTo('/dashboard/app/peripherals')
        },
        {
          label: 'Integrações',
          icon: BaseIconName.Share, // ícone de rede/conexão
          path: '/dashboard/settings/integrations-v2',
          name: 'integrations',
          command: navigateTo('/dashboard/settings/integrations-v2')
        },
        {
          label: 'Modelos de etiqueta',
          icon: BaseIconName.Tags, // ícone de etiqueta
          name: 'label-models',
          path: '/dashboard/settings/label-models',
          command: (): void => redirectToLabelModels()
        }
      ]
    },
    {
      label: 'Fiscal',
      icon: BaseIconName.Shield, // ícone de escudo
      agroup: true,
      items: [
        {
          label: 'Certificado digital',
          path: '/dashboard/settings/certificate',
          name: 'settings-certificate',
          command: navigateTo('/dashboard/settings/certificate')
        },
        {
          label: 'Configurações Gerais',
          path: '/dashboard/settings/fiscal-settings/general',
          name: 'fiscal-settings-general',
          command: navigateTo('/dashboard/settings/fiscal-settings/general')
        },
        {
          label: 'Padrões Fiscais',
          path: '/dashboard/settings/fiscal-settings/default-taxes',
          name: 'default-taxes',
          command: navigateTo('/dashboard/settings/fiscal-settings/default-taxes')
        },
        {
          label: 'Grupo do ICMS',
          path: '/dashboard/settings/fiscal-settings/icms-group',
          name: 'icms-group',
          command: navigateTo('/dashboard/settings/fiscal-settings/icms-group')
        },
        {
          label: 'Grupo do IPI',
          path: '/dashboard/settings/fiscal-settings/ipi-group',
          name: 'ipi-group',
          command: navigateTo('/dashboard/settings/fiscal-settings/ipi-group')
        },
        {
          label: 'Grupo do PIS/COFINS',
          path: '/dashboard/settings/fiscal-settings/pis-cofins-group',
          name: 'pis-cofins-group',
          command: navigateTo('/dashboard/settings/fiscal-settings/pis-cofins-group')
        },
        {
          label: 'Grupo ISSQN',
          path: '/dashboard/settings/fiscal-settings/issqn-groups',
          name: 'issqn-groups',
          command: navigateTo('/dashboard/settings/fiscal-settings/issqn-groups')
        },
        {
          label: 'Série de NF',
          path: '/dashboard/settings/fiscal-settings/invoice-series',
          name: 'invoice-series',
          command: navigateTo('/dashboard/settings/fiscal-settings/invoice-series')
        },
        {
          label: 'Tipo de NF emitida (PDV)',
          path: '/dashboard/settings/fiscal-settings/type-invoice-pos',
          name: 'type-invoice-pos',
          command: navigateTo('/dashboard/settings/fiscal-settings/type-invoice-pos')
        },
        {
          label: 'Informações Adicionais NFs',
          path: '/dashboard/settings/fiscal-settings/nfe-additional-info',
          name: 'nfe-additional-info',
          command: navigateTo('/dashboard/settings/fiscal-settings/nfe-additional-info')
        },
        {
          label: 'Natureza de Operações',
          path: '/dashboard/settings/fiscal-settings/nature-operations',
          name: 'nature-operations',
          command: navigateTo('/dashboard/settings/fiscal-settings/nature-operations')
        }
      ]
    },
    {
      label: 'Preferências do sistema',
      icon: BaseIconName.Cog, // ícone de escudo
      agroup: true,
      items: [
        {
          label: 'Geral',
          path: '/dashboard/settings/erp/general',
          name: 'settings-general',
          command: navigateTo('/dashboard/settings/erp/general')
        },
        {
          label: 'Produtos',
          path: '/dashboard/settings/erp/products',
          name: 'settngs-products',
          command: navigateTo('/dashboard/settings/erp/products')
        },
        {
          label: 'Autoatendimento',
          path: '/dashboard/settings/self-service',
          name: 'settings-self-service',
          command: navigateTo('/dashboard/settings/self-service')
        },
        {
          label: 'Estoque',
          path: '/dashboard/settings/erp/stock',
          name: 'settings-stock',
          command: navigateTo('/dashboard/settings/erp/stock')
        },
        {
          label: 'Clientes',
          path: '/dashboard/settings/erp/customers',
          name: 'settings-customers',
          command: navigateTo('/dashboard/settings/erp/customers')
        }
      ]
    }
  ]
}

function getCustomersMenu (navigateTo: (path: string) => () => void, _t: any): MenuItem[] {
  return [
    {
      items: [
        {
          label: 'Todos os clientes',
          icon: BaseIconName.Users, // ícone de dois usuários
          path: '/dashboard/customers',
          name: 'customers',
          command: navigateTo('/dashboard/customers')
        },
        {
          label: 'Categoria de clientes',
          icon: BaseIconName.IdCard, // ícone diferente de 'Todos os clientes'
          path: '/dashboard/crm/customer-categories', // ajustar path conforme necessário
          name: 'customer-categories',
          command: navigateTo('/dashboard/crm/customer-categories')
        }
      ]
    },
    {
      label: 'MARKETING & DIVULGAÇÃO',
      items: [
        {
          label: 'Campanhas / Marketing',
          icon: BaseIconName.Megaphone, // ícone de megafone
          path: '/dashboard/marketing-campaings',
          name: 'marketing-campaings',
          command: navigateTo('/dashboard/marketing-campaings')
        }
      ]
    }
  ]
}

function getPointOfSaleMenu (navigateTo: (path: string) => () => void, _t: any): MenuItem[] {
  return [
    {
      items: [
        {
          label: 'Cadastros',
          icon: BaseIconName.PlusCircle, // ícone de computador
          path: '/dashboard/fisical-operations/points-of-sale',
          name: 'points-of-sale',
          command: navigateTo('/dashboard/fisical-operations/points-of-sale')
        },
        {
          label: 'Gerenciar caixas',
          icon: BaseIconName.Desktop, // ícone de computador
          path: '/dashboard/fisical-operations/drawers',
          name: 'drawers',
          command: navigateTo('/dashboard/fisical-operations/drawers')
        },
        {
          label: 'KDS',
          icon: BaseIconName.ThLarge, // ícone de quadrados
          path: '/dashboard/fisical-operations/kds',
          name: 'kds',
          command: navigateTo('/dashboard/fisical-operations/kds')
        },
        {
          label: 'Mesas',
          icon: BaseIconName.ObjectsColumn, // ícone de mesa
          path: '/dashboard/fisical-operations/tables',
          name: 'tables',
          command: navigateTo('/dashboard/fisical-operations/tables?from=fisical-operations')
        },
        {
          label: 'Comandas',
          icon: BaseIconName.Receipt,
          path: '/dashboard/fisical-operations/commands',
          name: 'commands',
          command: navigateTo('/dashboard/fisical-operations/commands?from=fisical-operations')
        }
      ]
    },
    {
      label: 'Configurações',
      icon: BaseIconName.Cog,
      agroup: true,
      items: [
        {
          label: 'Geral',
          path: '/dashboard/fisical-operations/terminal/general',
          name: 'terminal-general',
          command: navigateTo('/dashboard/fisical-operations/terminal/general')
        },
        {
          label: 'Modos de pedidos',
          path: '/dashboard/fisical-operations/terminal/ordering-modes',
          name: 'terminal-ordering-modes',
          command: navigateTo('/dashboard/fisical-operations/terminal/ordering-modes')
        },
        {
          label: 'Consignado',
          path: '/dashboard/fisical-operations/terminal/consigned',
          name: 'terminal-consigned',
          command: navigateTo('/dashboard/fisical-operations/terminal/consigned')
        },
        {
          label: 'Crediário',
          path: '/dashboard/fisical-operations/terminal/installment-plan',
          name: 'terminal-installment-plan',
          command: navigateTo('/dashboard/fisical-operations/terminal/installment-plan')
        },
        {
          label: 'KDS',
          path: '/dashboard/fisical-operations/terminal/kds',
          name: 'terminal-kds',
          command: navigateTo('/dashboard/fisical-operations/terminal/kds')
        },
        {
          label: 'Painel de senhas',
          path: '/dashboard/fisical-operations/passwords-panel',
          name: 'passwords-panel',
          command: navigateTo('/dashboard/fisical-operations/passwords-panel')
        },
        {
          label: 'Recibos e notas impressas',
          path: '/dashboard/fisical-operations/terminal/receipts-and-printed-notes',
          name: 'receipts-and-printed-notes',
          command: navigateTo('/dashboard/fisical-operations/terminal/receipts-and-printed-notes')
        },
        {
          label: 'Troca & Devolução',
          path: '/dashboard/fisical-operations/terminal/exchange-and-returning',
          name: 'exchange-and-returning',
          command: navigateTo('/dashboard/fisical-operations/terminal/exchange-and-returning')
        }
      ]
    }
  ]
}
